// components/NotifRecipientModal/index.tsx
// 通知先設定モーダル（＋送信確認モーダル）の汎用コンポーネント
//
// 現在の使用箇所:
//   - src/components/FollowListModal/index.tsx（フォロー詳細モーダル内）
//
// 将来的な差し替え候補:
//   - src/pages/BusinessCardDetailPage.tsx（フォロー追加モーダル内）
//     差し替え時: submitLabel="登録"、showConfirm は不要（false のまま）
//     zIndex={1002}、wrapClassName="follow-notif-modal" を指定すること
//   - src/pages/FollowRegistrationPage.tsx（フォロー登録フォーム内）
//     差し替え時: submitLabel="登録"、showConfirm は不要（false のまま）

import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, Checkbox, Input } from 'antd';
import { PlusOutlined, CloseOutlined, MailOutlined, BellOutlined, CalendarOutlined, DownOutlined } from '@ant-design/icons';
import { MODAL_Z } from '../../utils/modalZIndex';
import { NotifRecipient, NotifRecipientWithDetails } from '../../types/follow';

export type { NotifRecipient, NotifRecipientWithDetails } from '../../types/follow';

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

export interface NotifRecipientContactUser {
  userno: number;
  username: string;
  deptname: string;
}

export interface AlertSettings {
  preAlert: string;    // "" (未設定) | "1"〜"30"
  reAlert: string;     // "" (未設定) | "1"〜"7"
  reAlertFlag: boolean;
}

export interface NotifRecipientModalProps {
  /** モーダルの表示状態 */
  open: boolean;
  /** キャンセル時のコールバック（モーダルを閉じる処理を渡すこと） */
  onCancel: () => void;
  /**
   * 送信確定時のコールバック
   * - showConfirm=false のとき: 送信ボタン押下で呼ばれる
   * - showConfirm=true のとき: 確認モーダルの確定ボタン押下で呼ばれる
   * @param recipients 通知先ユーザー一覧
   * @param teamsChannelUrls 選択されたTeamsチャネルURL一覧
   * @param emailAddresses 直接入力されたメールアドレス一覧
   * @param alertSettings アラート設定（showAlertSettings=true の場合のみ）
   */
  onSubmit: (recipients: NotifRecipient[], teamsChannelUrls: string[], emailAddresses: string[], alertSettings?: AlertSettings) => void;
  /**
   * アラート設定セクションを表示するか（予定/ToDoが有効な場合に true を渡す）
   */
  showAlertSettings?: boolean;
  /**
   * アラート設定の初期値（編集時に DB から取得した値を渡す）
   * 省略時は新規登録用デフォルト（事前=未設定、再=1日後、繰り返し=ON）を使用
   */
  initialAlertSettings?: Partial<AlertSettings>;
  /**
   * 送信先候補ユーザー一覧
   * （BusinessCardDetailPage の contactUsers 相当）
   */
  contactUsers: NotifRecipientContactUser[];
  /**
   * 送信先候補Teamsチャネル一覧（省略または空配列時はTeamsセクションを非表示）
   * モーダルを開く前に取得して渡すこと
   * defaultFlag: true のチャネルはモーダルを開いたとき初期選択済みになる
   */
  teamsChannels?: { url: string; name: string; defaultFlag?: boolean }[];
  /** モーダルタイトル（デフォルト: "通知先設定"） */
  title?: string;
  /**
   * 送信ボタンのラベル（デフォルト: "送信"）
   * BusinessCardDetailPage / FollowRegistrationPage では "登録" に変更すること
   */
  submitLabel?: string;
  /**
   * 送信前に確認モーダルを表示するか（デフォルト: false）
   * FollowListModal のように別途確認ステップが必要な場合に true にすること
   */
  showConfirm?: boolean;
  /** モーダルの zIndex（デフォルト: MODAL_Z.L2） */
  zIndex?: number;
  /** モーダルを開いたとき初期選択済みにする受信者（省略時は空） */
  initialRecipients?: NotifRecipientWithDetails[];
}

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NotifRecipientModal: React.FC<NotifRecipientModalProps> = ({
  open,
  onCancel,
  onSubmit,
  contactUsers,
  teamsChannels = [],
  title = '通知先設定',
  submitLabel = '送信',
  showConfirm = false,
  zIndex = MODAL_Z.L2,
  initialRecipients = [],
  showAlertSettings = false,
  initialAlertSettings,
}) => {
  // 担当者送信先
  const [recipients, setRecipients] = useState<NotifRecipient[]>([]);
  const [adding, setAdding] = useState(false);
  // Teamsチャネル選択状態（defaultFlag=true のチャネルはモーダルオープン時に初期選択）
  const [selectedTeamsChannelUrls, setSelectedTeamsChannelUrls] = useState<string[]>([]);

  // モーダルを開くたびに initialRecipients を recipients へ反映する
  useEffect(() => {
    if (open) {
      setRecipients(initialRecipients.map(r => ({ userno: r.userno, urgent: r.urgent })));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // TODO: Teams通知を実装する際はコメントアウトを解除（defaultFlag チャネルの初期選択）
  // useEffect(() => {
  //   if (open && teamsChannels) {
  //     setSelectedTeamsChannelUrls(
  //       teamsChannels.filter(ch => ch.defaultFlag).map(ch => ch.url)
  //     );
  //   }
  // }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // 外部メールアドレス直接入力
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  // 確認モーダル
  const [confirmVisible, setConfirmVisible] = useState(false);
  // アラート設定（初期値: 事前=未設定、再=1日後、繰り返し=ON）
  const [preAlert, setPreAlert] = useState<string>('');
  const [reAlert, setReAlert] = useState<string>('1');
  const [reAlertFlag, setReAlertFlag] = useState<boolean>(true);
  const [alertSettingsOpen, setAlertSettingsOpen] = useState<boolean>(false);

  const reset = () => {
    setRecipients([]);
    setAdding(false);
    setSelectedTeamsChannelUrls([]);
    setEmailAddresses([]);
    setEmailInput('');
    setEmailError('');
    setConfirmVisible(false);
    setPreAlert('');
    setReAlert('1');
    setReAlertFlag(true);
    setAlertSettingsOpen(false);
  };

  const handleCancel = () => {
    onCancel();
    reset();
  };

  // モーダルが開くたびにアラート設定を初期化（編集時は DB 値、新規時はデフォルト）
  useEffect(() => {
    if (open) {
      setPreAlert(initialAlertSettings?.preAlert ?? '');
      setReAlert(initialAlertSettings?.reAlert ?? '1');
      setReAlertFlag(initialAlertSettings?.reAlertFlag ?? true);
      setAlertSettingsOpen(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildAlertSettings = (): AlertSettings | undefined => {
    if (!showAlertSettings) return undefined;
    return { preAlert, reAlert, reAlertFlag };
  };

  const handleReAlertChange = (value: string) => {
    setReAlert(value);
    if (value === '') setReAlertFlag(false);
  };

  const handleSubmitClick = () => {
    if (showConfirm) {
      setConfirmVisible(true);
    } else {
      onSubmit(recipients, selectedTeamsChannelUrls, emailAddresses, buildAlertSettings());
      reset();
    }
  };

  const handleConfirm = () => {
    setConfirmVisible(false);
    onSubmit(recipients, selectedTeamsChannelUrls, emailAddresses, buildAlertSettings());
    reset();
  };

  // TODO: メールアドレス通知を実装する際はコメントアウトを解除
  // const handleAddEmail = () => {
  //   const trimmed = emailInput.trim();
  //   if (!trimmed) return;
  //   if (!EMAIL_PATTERN.test(trimmed)) {
  //     setEmailError('正しいメールアドレスを入力してください');
  //     return;
  //   }
  //   if (emailAddresses.includes(trimmed)) {
  //     setEmailError('すでに追加済みのメールアドレスです');
  //     return;
  //   }
  //   setEmailAddresses(prev => [...prev, trimmed]);
  //   setEmailInput('');
  //   setEmailError('');
  // };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddEmail = () => {}; // TODO: メールアドレス通知実装時は上記の実装に差し替え

  const allSelected =
    contactUsers.length > 0 &&
    contactUsers.every(u => recipients.some(r => r.userno === u.userno));
  const someSelected = recipients.length > 0 && !allSelected;

  /** userno から表示用ユーザー情報を解決する（contactUsers 優先、無ければ initialRecipients から） */
  const resolveRecipientUser = (userno: number): { username?: string; deptname?: string } | undefined =>
    contactUsers.find(c => c.userno === userno)
    ?? initialRecipients.find(r => r.userno === userno);

  return (
    <>
      {/* 通知先設定モーダル */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: zIndex - 1 }} />
      )}
      <Modal
        title={title}
        open={open}
        onCancel={handleCancel}
        width={480}
        centered
        zIndex={zIndex}
        mask={false}
        maskClosable={false}
        styles={{ body: { paddingBottom: 32 } }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button onClick={handleCancel}>キャンセル</Button>
            <Button type="primary" onClick={handleSubmitClick}>
              {submitLabel}
            </Button>
          </div>
        }
      >
        <p style={{ marginBottom: 16 }}>
          データを保存します。<br />
          フォロー内容を他の人へ通知したい場合は、下記より設定してください。
        </p>

        {/* 担当者送信先セクション */}
        <div>
          <span style={{ fontWeight: 'bold' }}>送信先</span>

          {/* 追加済み送信先テーブル */}
          {recipients.length > 0 && (
            <div style={{ marginTop: 8, marginBottom: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 1fr 36px', padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontSize: 13, fontWeight: 'bold', color: 'rgba(0,0,0,0.65)' }}>
                <div style={{ textAlign: 'center' }}>至急</div>
                <div>部署</div>
                <div>名前</div>
                <div />
              </div>
              {recipients.map((recipient) => {
                const u = resolveRecipientUser(recipient.userno);
                return (
                  <div
                    key={recipient.userno}
                    style={{ display: 'grid', gridTemplateColumns: '52px 1fr 1fr 36px', padding: '6px 8px', borderBottom: '1px solid #f5f5f5', alignItems: 'center', backgroundColor: recipient.urgent ? '#fff1f0' : undefined }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <Checkbox
                        className={recipient.urgent ? 'urgent-checkbox' : undefined}
                        checked={recipient.urgent}
                        onChange={(e) =>
                          setRecipients(prev =>
                            prev.map(r =>
                              r.userno === recipient.userno ? { ...r, urgent: e.target.checked } : r
                            )
                          )
                        }
                      />
                    </div>
                    <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u?.deptname || '本社'}
                    </div>
                    <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u?.username || `(ID:${recipient.userno})`}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <button
                        onClick={() =>
                          setRecipients(prev => prev.filter(r => r.userno !== recipient.userno))
                        }
                        style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#bfbfbf', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, color: '#fff' }}
                      >
                        <CloseOutlined style={{ fontSize: 10 }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 送信先追加ボタン or 複数選択ドロップダウン */}
          {!adding ? (
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Button icon={<PlusOutlined />} onClick={() => setAdding(true)}>
                送信先追加
              </Button>
              {recipients.length > 0 && (
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => setRecipients([])}
                  style={{ backgroundColor: '#f5f5f5', borderColor: '#d9d9d9' }}
                >
                  クリア
                </Button>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="送信先を選択"
                defaultOpen
                value={recipients.map(r => r.userno)}
                onChange={(values: number[]) => {
                  setRecipients(prev => {
                    const kept = prev.filter(r => values.includes(r.userno));
                    const added = values
                      .filter(v => !prev.some(r => r.userno === v))
                      .map(v => ({ userno: v, urgent: false }));
                    return [...kept, ...added];
                  });
                }}
                onDropdownVisibleChange={(open) => { if (!open) setAdding(false); }}
                maxTagCount={0}
                maxTagPlaceholder={(omitted) => `${omitted.length}件選択中`}
                popupClassName="notif-recipient-dropdown"
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      <Checkbox
                        className="notif-select-all-checkbox"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => {
                          setRecipients(
                            e.target.checked
                              ? contactUsers.map(u => ({ userno: u.userno, urgent: false }))
                              : []
                          );
                        }}
                      >
                        全選択
                      </Checkbox>
                    </div>
                    {menu}
                  </>
                )}
              >
                {contactUsers.map(u => (
                  <Select.Option key={u.userno} value={u.userno}>
                    {u.username} ({u.deptname})
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}
        </div>

        {/* TODO: メールアドレス通知を実装する際は false を削除してセクションを有効化 */}
        {false && (
        <div style={{ marginTop: 20 }}>
          <span style={{ fontWeight: 'bold' }}>メール通知（外部宛）</span>

          {/* 追加済みメールアドレス一覧 */}
          {emailAddresses.length > 0 && (
            <div style={{ marginTop: 8, marginBottom: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
              {emailAddresses.map((email) => (
                <div
                  key={email}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #f5f5f5' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, overflow: 'hidden' }}>
                    <MailOutlined style={{ color: '#8c8c8c', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
                  </div>
                  <button
                    onClick={() => setEmailAddresses(prev => prev.filter(e => e !== email))}
                    style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#bfbfbf', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, color: '#fff', flexShrink: 0, marginLeft: 8 }}
                  >
                    <CloseOutlined style={{ fontSize: 10 }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* メールアドレス入力フォーム */}
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Input
              prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="example@domain.com"
              value={emailInput}
              onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
              onPressEnter={handleAddEmail}
              style={{ flex: 1 }}
              status={emailError ? 'error' : undefined}
            />
            <Button icon={<PlusOutlined />} onClick={handleAddEmail}>
              追加
            </Button>
          </div>
          {emailError && (
            <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{emailError}</p>
          )}
        </div>
        )}

        {/* TODO: Teams通知を実装する際は false を削除してセクションを有効化 */}
        {false && teamsChannels.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <span style={{ fontWeight: 'bold' }}>Teamsチャネル通知</span>
            <div style={{ marginTop: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
              {teamsChannels.map((channel) => (
                <div
                  key={channel.url}
                  style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f5f5f5' }}
                >
                  <Checkbox
                    checked={selectedTeamsChannelUrls.includes(channel.url)}
                    onChange={(e) => {
                      setSelectedTeamsChannelUrls(prev =>
                        e.target.checked
                          ? [...prev, channel.url]
                          : prev.filter(url => url !== channel.url)
                      );
                    }}
                    style={{ marginRight: 10 }}
                  />
                  <span style={{ fontSize: 14 }}>{channel.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* アラート設定（予定/ToDoが有効な場合に表示） */}
        {showAlertSettings && (
          <div style={{ marginTop: 20 }}>
            <button
              onClick={() => setAlertSettingsOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', width: '100%', textAlign: 'left' }}
            >
              <span style={{ fontWeight: 'bold', fontSize: 14 }}>アラート設定</span>
              <DownOutlined
                style={{ fontSize: 11, color: '#8c8c8c', transition: 'transform 0.2s', transform: alertSettingsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            {alertSettingsOpen && (
            <div style={{ marginTop: 12, position: 'relative', paddingLeft: 20 }}>
              {/* タイムライン縦線 */}
              <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: '#d9d9d9' }} />

              {/* 事前アラート設定 */}
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#1677ff', position: 'absolute', left: -17, top: 6 }} />
                <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 6, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <BellOutlined style={{ color: '#1677ff', fontSize: 14 }} />
                    <span style={{ fontWeight: 'bold', color: '#1677ff', fontSize: 13 }}>事前アラート設定</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* width固定divで包むことでBCDのCSS(.ant-select { width:100%!important })の影響を回避 */}
                    <div style={{ width: 90, flexShrink: 0 }}>
                      <Select
                        value={preAlert}
                        onChange={setPreAlert}
                        style={{ width: '100%' }}
                        size="small"
                      >
                        <Select.Option value="">未設定</Select.Option>
                        {Array.from({ length: 30 }, (_, i) => i + 1).map(d => (
                          <Select.Option key={d} value={String(d)}>{d}日前</Select.Option>
                        ))}
                      </Select>
                    </div>
                    <span style={{ fontSize: 12, color: '#595959' }}>にアラートを送信</span>
                  </div>
                </div>
              </div>

              {/* フォロー日基準点 */}
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', border: '2px solid #bfbfbf', position: 'absolute', left: -20, top: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CalendarOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                </div>
                <div style={{ color: '#8c8c8c', fontSize: 13, paddingLeft: 4, lineHeight: '20px' }}>フォロー日</div>
              </div>

              {/* 再アラート設定 */}
              <div style={{ position: 'relative' }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fa8c16', position: 'absolute', left: -17, top: 6 }} />
                <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <BellOutlined style={{ color: '#fa8c16', fontSize: 14 }} />
                    <span style={{ fontWeight: 'bold', color: '#fa8c16', fontSize: 13 }}>再アラート設定</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {/* width固定divで包むことでBCDのCSS(.ant-select { width:100%!important })の影響を回避 */}
                    <div style={{ width: 90, flexShrink: 0 }}>
                      <Select
                        value={reAlert}
                        onChange={handleReAlertChange}
                        style={{ width: '100%' }}
                        size="small"
                      >
                        <Select.Option value="">未設定</Select.Option>
                        {Array.from({ length: 7 }, (_, i) => i + 1).map(d => (
                          <Select.Option key={d} value={String(d)}>{d}日後</Select.Option>
                        ))}
                      </Select>
                    </div>
                    <span style={{ fontSize: 12, color: '#595959' }}>にアラートを送信</span>
                    <Checkbox
                      checked={reAlertFlag}
                      disabled={reAlert === ''}
                      onChange={e => setReAlertFlag(e.target.checked)}
                      style={{ marginLeft: 4 }}
                    >
                      繰り返し
                    </Checkbox>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </Modal>

      {/* 送信確認モーダル（showConfirm=true のときのみ使用） */}
      {showConfirm && (
        <>
        {confirmVisible && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: zIndex }} />
        )}
        <Modal
          title="確認"
          open={confirmVisible}
          onCancel={() => setConfirmVisible(false)}
          centered
          mask={false}
          maskClosable={false}
          zIndex={zIndex + 1}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setConfirmVisible(false)}>キャンセル</Button>
              <Button type="primary" onClick={handleConfirm}>
                {submitLabel}
              </Button>
            </div>
          }
        >
          <p>通知を送信しますか？</p>
          {recipients.length > 0 && (
            <p style={{ color: '#8c8c8c', fontSize: 13 }}>
              送信先：
              {recipients
                .map(r => resolveRecipientUser(r.userno)?.username ?? `(ID:${r.userno})`)
                .join('、')}
            </p>
          )}
          {/* TODO: メールアドレス通知実装時はコメントアウトを解除 */}
          {false && emailAddresses.length > 0 && (
            <p style={{ color: '#8c8c8c', fontSize: 13 }}>
              メール：{emailAddresses.join('、')}
            </p>
          )}
          {/* TODO: Teams通知実装時はコメントアウトを解除 */}
          {false && selectedTeamsChannelUrls.length > 0 && (
            <p style={{ color: '#8c8c8c', fontSize: 13 }}>
              Teamsチャネル：
              {selectedTeamsChannelUrls
                .map(url => teamsChannels.find(c => c.url === url)?.name ?? url)
                .join('、')}
            </p>
          )}
        </Modal>
        </>
      )}
    </>
  );
};

export default NotifRecipientModal;
